import { useEffect, useRef, useState } from "react";
import {
  DEF,
  terminalPos,
  PLC_TERMINAL_HIT_R,
  PLC_HIDDEN_TERMINALS,
} from "../../plc-sim/library";
import { tk } from "../../plc-sim/electrical";
import { store, useTick } from "../../plc-sim/store";
import { wireColor, WIRE_COLORS, type DeviceInst } from "../../plc-sim/types";
import { DeviceBody, SvgDefs } from "./DeviceSvg";

interface View {
  x: number;
  y: number;
  k: number;
}

export function PanelCanvas({
  selected,
  setSelected,
  selectedWire,
  setSelectedWire,
  wireColorId,
  focusId,
}: {
  selected: string | null;
  setSelected: (id: string | null) => void;
  selectedWire: string | null;
  setSelectedWire: (id: string | null) => void;
  wireColorId: string;
  focusId?: string | null;
}) {
  useTick();
  const project = store.project;
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [view, setView] = useState<View>(() => {
    const saved = store.project.view;
    return saved &&
      Number.isFinite(saved.x) &&
      Number.isFinite(saved.y) &&
      Number.isFinite(saved.k) &&
      saved.k > 0
      ? { x: saved.x, y: saved.y, k: Math.min(3, Math.max(0.15, saved.k)) }
      : { x: 0, y: 0, k: 0.7 };
  });
  const viewRef = useRef(view);
  viewRef.current = view;
  useEffect(() => {
    const t = setTimeout(() => {
      if (Number.isFinite(view.x) && Number.isFinite(view.y) && Number.isFinite(view.k) && view.k > 0) {
        store.setView(view);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [view]);
  const [pending, setPending] = useState<string | null>(null);
  const drag = useRef<{ id: string; dx: number; dy: number } | null>(null);
  const pan = useRef<{ x: number; y: number; vx: number; vy: number } | null>(null);
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const pinch = useRef<{ dist: number; k: number; cx: number; cy: number } | null>(null);
  const wirePathDrag = useRef<{
    id: string;
    pointerId: number;
    startX: number;
    startY: number;
    controlX: number;
    controlY: number;
  } | null>(null);

  const clampK = (k: number) => {
    if (!Number.isFinite(k)) return 0.7;
    return Math.min(3, Math.max(0.15, k));
  };

  const toWorld = (clientX: number, clientY: number) => {
    const el = svgRef.current;
    const v = viewRef.current;
    if (!el || !Number.isFinite(v.k) || v.k <= 0) return null;
    const r = el.getBoundingClientRect();
    const x = (clientX - r.left - v.x) / v.k;
    const y = (clientY - r.top - v.y) / v.k;
    return Number.isFinite(x) && Number.isFinite(y) ? { x, y } : null;
  };

  const defaultWireControl = (p1: { x: number; y: number }, p2: { x: number; y: number }) => {
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const len = Math.max(1, Math.hypot(dx, dy));
    const bend = Math.min(72, Math.max(24, len * 0.12));
    return {
      x: (p1.x + p2.x) / 2 - (dy / len) * bend,
      y: (p1.y + p2.y) / 2 + (dx / len) * bend,
    };
  };

  const wireHasVisibleEndpoints = (wire: { from: string; to: string }) => {
    const endpointVisible = (key: string) => {
      const splitAt = key.indexOf(":");
      if (splitAt < 0) return false;
      const devId = key.slice(0, splitAt);
      const termId = key.slice(splitAt + 1);
      const dev = project.devices.find((d) => d.id === devId);
      if (!dev) return false;
      // The supplied PLC photograph has no honest physical screw for these
      // model-only supply/reference terminals. Keep them in the electrical
      // model, but never draw a wire to a fake point on the photo.
      if (dev.type === "plc" && PLC_HIDDEN_TERMINALS.has(termId)) return false;
      return Boolean(DEF[dev.type]?.terminals.some((t) => t.id === termId));
    };
    return endpointVisible(wire.from) && endpointVisible(wire.to);
  };

  /* wheel zoom (non-passive) */
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const dy = e.deltaY * (e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? 100 : 1);
      setView((v) => {
        const baseK = clampK(v.k);
        const next = clampK(baseK * Math.exp(-dy * 0.0015));
        const r = el.getBoundingClientRect();
        const px = e.clientX - r.left;
        const py = e.clientY - r.top;
        const s = next / baseK;
        const x = px - (px - v.x) * s;
        const y = py - (py - v.y) * s;
        return Number.isFinite(x) && Number.isFinite(y) ? { k: next, x, y } : v;
      });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  const zoomBy = (f: number) =>
    setView((v) => {
      const el = svgRef.current;
      if (!el) return v;
      const r = el.getBoundingClientRect();
      const baseK = clampK(v.k);
      const next = clampK(baseK * f);
      const px = r.width / 2;
      const py = r.height / 2;
      const s = next / baseK;
      const x = px - (px - v.x) * s;
      const y = py - (py - v.y) * s;
      return Number.isFinite(x) && Number.isFinite(y) ? { k: next, x, y } : v;
    });

  const fit = () => {
    const el = svgRef.current;
    if (!el || project.devices.length === 0) return;
    const r = el.getBoundingClientRect();
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;
    for (const d of project.devices) {
      const def = DEF[d.type];
      if (!def) continue;
      minX = Math.min(minX, d.x - 20);
      minY = Math.min(minY, d.y - 20);
      maxX = Math.max(maxX, d.x + def.w + 20);
      maxY = Math.max(maxY, d.y + def.h + 40);
    }
    const spanX = Math.max(1, maxX - minX);
    const spanY = Math.max(1, maxY - minY);
    const k = clampK(Math.min(r.width / spanX, r.height / spanY, 1.5));
    const x = -minX * k + 10;
    const y = -minY * k + 10;
    if (Number.isFinite(x) && Number.isFinite(y)) setView({ k, x, y });
  };

  const zoomTo = (k: number) =>
    setView((v) => {
      const el = svgRef.current;
      if (!el) return v;
      const r = el.getBoundingClientRect();
      const next = clampK(k);
      const baseK = clampK(v.k);
      const px = r.width / 2;
      const py = r.height / 2;
      const s = next / baseK;
      const x = px - (px - v.x) * s;
      const y = py - (py - v.y) * s;
      return Number.isFinite(x) && Number.isFinite(y) ? { k: next, x, y } : v;
    });

  /* center on a device when asked from outside (error panel / I/O table) */
  useEffect(() => {
    if (!focusId) return;
    const d = store.project.devices.find((x) => x.id === focusId);
    const el = svgRef.current;
    if (!d || !el) return;
    const def = DEF[d.type];
    const r = el.getBoundingClientRect();
    setView((v) => {
      const k = clampK(v.k);
      const x = r.width / 2 - (d.x + (def?.w ?? 0) / 2) * k;
      const y = r.height / 2 - (d.y + (def?.h ?? 0) / 2) * k;
      return Number.isFinite(x) && Number.isFinite(y) ? { k, x, y } : v;
    });
  }, [focusId]);

  /* ---------------------------------------------------------- pointers */
  const onPointerDown = (e: React.PointerEvent) => {
    try {
      (e.target as Element).setPointerCapture?.(e.pointerId);
    } catch {
      // Pointer capture can fail if the browser has already cancelled a
      // touch/pinch pointer. Never let that take down the whole workspace.
    }
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      pinch.current = {
        dist: Math.max(1, Math.hypot(a!.x - b!.x, a!.y - b!.y)),
        k: clampK(viewRef.current.k),
        cx: (a!.x + b!.x) / 2,
        cy: (a!.y + b!.y) / 2,
      };
      drag.current = null;
      pan.current = null;
      wirePathDrag.current = null;
    }
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (pointers.current.has(e.pointerId))
      pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pinch.current && pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      const dist = Math.hypot(a!.x - b!.x, a!.y - b!.y);
      const ncx = (a!.x + b!.x) / 2;
      const ncy = (a!.y + b!.y) / 2;
      const panDx = ncx - pinch.current.cx;
      const panDy = ncy - pinch.current.cy;
      pinch.current.cx = ncx;
      pinch.current.cy = ncy;
      const next = clampK((pinch.current.k * Math.max(1, dist)) / Math.max(1, pinch.current.dist));
      setView((v) => {
        const el = svgRef.current;
        if (!el) return v;
        const r = el.getBoundingClientRect();
        const px = ncx - r.left;
        const py = ncy - r.top;
        const baseK = clampK(v.k);
        const s = next / baseK;
        const x = px - (px - v.x) * s + panDx;
        const y = py - (py - v.y) * s + panDy;
        return Number.isFinite(x) && Number.isFinite(y) ? { k: next, x, y } : v;
      });
      return;
    }
    if (drag.current) {
      const w = toWorld(e.clientX, e.clientY);
      if (!w) return;
      store.moveDevice(drag.current.id, w.x - drag.current.dx, w.y - drag.current.dy);
      return;
    }
    const activePan = pan.current;
    if (activePan) {
      // Snapshot the gesture before scheduling the React state update.
      // Pointer-up/cancel may clear pan.current before this updater runs.
      const clientX = e.clientX;
      const clientY = e.clientY;
      setView((v) => {
        const x = activePan.vx + (clientX - activePan.x);
        const y = activePan.vy + (clientY - activePan.y);
        return Number.isFinite(x) && Number.isFinite(y) ? { ...v, x, y } : v;
      });
    }
  };

  const endPointer = (e: React.PointerEvent) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinch.current = null;
    drag.current = null;
    pan.current = null;
  };

  const bgDown = (e: React.PointerEvent) => {
    if (pointers.current.size > 1) return;
    pan.current = { x: e.clientX, y: e.clientY, vx: viewRef.current.x, vy: viewRef.current.y };
    setSelected(null);
    setSelectedWire(null);
    setPending(null);
  };

  const deviceDown = (e: React.PointerEvent, d: DeviceInst) => {
    e.stopPropagation();
    setSelected(d.id);
    setSelectedWire(null);
    if (store.status === "RUN") {
      actuate(d, true);
      return;
    }
    const w = toWorld(e.clientX, e.clientY);
    if (!w) return;
    store.beginMove();
    drag.current = { id: d.id, dx: w.x - d.x, dy: w.y - d.y };
  };

  const deviceUp = (d: DeviceInst) => {
    if (store.status === "RUN") actuate(d, false);
  };

  const actuate = (d: DeviceInst, down: boolean) => {
    const def = DEF[d.type];
    if (!def?.action) return;
    if (def.action === "momentary") d.state["pressed"] = down;
    else if (down) {
      if (def.action === "toggle") {
        if (d.type === "overload") d.state["tripped"] = !d.state["tripped"];
        else if (d.type === "psu24" || d.type === "src220" || d.type === "src380")
          d.state["off"] = !d.state["off"];
        else if (d.type === "mcb" || d.type === "mcb1" || d.type === "mcb3") d.state["on"] = d.state["on"] === false;
        else d.state["on"] = !d.state["on"];
      } else if (def.action === "latch") d.state["latched"] = !d.state["latched"];
      else if (def.action === "selector") d.state["man"] = !d.state["man"];
    }
    store.touch();
    // Make panel input/output changes visible in LAD/FBD immediately while monitoring.
    if (store.status === "RUN") store.scan();
  };

  const clickTerminal = (devId: string, term: string) => {
    // A terminal always starts/continues wiring, even if one or many wires are
    // already attached to it. This is essential for realistic branch wiring.
    setSelectedWire(null);
    const key = tk(devId, term);
    if (!pending) {
      setPending(key);
      return;
    }
    if (pending === key) {
      setPending(null);
      return;
    }
    store.addWire(pending, key, wireColorId);
    setPending(null);
  };

  const live = store.elec.live;

  const pendingLabel = (() => {
    if (!pending) return "";
    const [devId, termId] = pending.split(":");
    const dev = project.devices.find((x) => x.id === devId);
    const term = dev ? DEF[dev.type]?.terminals.find((x) => x.id === termId) : undefined;
    return term?.label ?? termId ?? "";
  })();

  return (
    <div className="relative h-full w-full overflow-hidden bg-[var(--panel-bg)]">
      <img
        src="/engineer-logo.png"
        alt=""
        aria-hidden="true"
        draggable={false}
        className="pointer-events-none absolute left-1/2 top-1/2 z-0 w-[min(42vw,360px)] max-w-[62vw] -translate-x-1/2 -translate-y-1/2 select-none opacity-[0.10] mix-blend-multiply"
      />
      <svg
        ref={svgRef}
        data-panel-canvas="true"
        data-view-x={view.x}
        data-view-y={view.y}
        data-view-k={view.k}
        className="relative z-10 h-full w-full touch-none select-none"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endPointer}
        onPointerCancel={endPointer}
      >
        <SvgDefs />
        <defs>
          <pattern id="grid" width={24} height={24} patternUnits="userSpaceOnUse">
            <path d="M24 0H0V24" fill="none" stroke="var(--panel-grid)" strokeWidth={1} />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid)" onPointerDown={bgDown} />

        <g transform={`translate(${view.x},${view.y}) scale(${view.k})`}>
          {/* devices */}
          {project.devices.map((d) => {
            const def = DEF[d.type];
            if (!def) return null;
            const sel = selected === d.id;
            return (
              <g key={d.id} transform={`translate(${d.x},${d.y})`}>
                <g
                  onPointerDown={(e) => deviceDown(e, d)}
                  onPointerUp={() => deviceUp(d)}
                  onPointerLeave={() => store.status === "RUN" && deviceUp(d)}
                  style={{ cursor: store.status === "RUN" ? "pointer" : "move" }}
                >
                  <DeviceBody d={d} />

                </g>
                {sel && (
                  <rect
                    x={-6}
                    y={-6}
                    width={def.w + 12}
                    height={def.h + 12}
                    rx={8}
                    fill="none"
                    stroke="var(--color-primary)"
                    strokeWidth={2}
                    strokeDasharray="6 4"
                  />
                )}
                <text
                  x={def.w / 2}
                  y={def.h + 18}
                  textAnchor="middle"
                  fontSize={12}
                  fill="var(--panel-label)"
                >
                  {d.label}
                </text>
                {def.terminals.map((t) => {
                  // Never invent a visible point where this exact photograph
                  // has no physical screw head.
                  if (d.type === "plc" && PLC_HIDDEN_TERMINALS.has(t.id)) return null;
                  const key = tk(d.id, t.id);
                  const isLive = live.has(key);
                  const isPending = pending === key;
                  const wired = project.wires.some((w) => w.from === key || w.to === key);
                  const show = sel || isPending || isLive || wired || pending !== null;
                  return (
                    <g
                      key={t.id}
                      transform={`translate(${t.x},${t.y})`}
                      onPointerDown={(e) => {
                        e.stopPropagation();
                        clickTerminal(d.id, t.id);
                      }}
                      style={{ cursor: "crosshair" }}
                    >
                      {(() => {
                        const isMcbPhoto = d.type === "mcb1" || d.type === "mcb" || d.type === "mcb3";
                        const photoTerminal = d.type === "plc" || d.type === "contactor" || d.type === "relay" || d.type === "overload" || d.type === "timer" || isMcbPhoto;
                        const hitR = d.type === "plc" ? PLC_TERMINAL_HIT_R : d.type === "contactor" ? 9 : d.type === "relay" ? 5.2 : d.type === "overload" ? 7.8 : d.type === "timer" ? 6.5 : isMcbPhoto ? 8.5 : 16;
                        const terminalLit = d.type === "contactor" || d.type === "relay" || d.type === "overload" || d.type === "timer" || isMcbPhoto ? wired || isLive : isLive;
                        return (
                          <>
                            <circle r={hitR} fill="transparent" />
                            {photoTerminal ? (
                              /* Photo-based devices use the real screw head as the UI.
                                 There is no permanent blue/cyan ring over the photo. */
                              <>
                                {terminalLit && (
                                  <circle
                                    r={d.type === "contactor" || d.type === "relay" || d.type === "overload" || d.type === "timer" || isMcbPhoto ? hitR : Math.max(3.8, hitR - 1.2)}
                                    fill="#32e34f"
                                    fillOpacity={1}
                                    stroke="#c9ffd0"
                                    strokeWidth={d.type === "contactor" ? 1.0 : 0.8}
                                  />
                                )}
                                {isPending && !terminalLit && (
                                  <circle
                                    r={Math.max(4.2, hitR - 0.5)}
                                    fill="transparent"
                                    stroke="#ffb020"
                                    strokeWidth={1.4}
                                    strokeDasharray="2 2"
                                  />
                                )}
                              </>
                            ) : (
                              <circle
                                r={isPending ? 9 : 6}
                                fill={isLive ? "#39e04f" : "#c3c9ce"}
                                stroke={isPending ? "var(--color-primary)" : "#5c666d"}
                                strokeWidth={isPending ? 3 : 1}
                                opacity={show ? 1 : 0.18}
                              />
                            )}
                            {show && !photoTerminal && (
                              <text
                                y={t.y > def.h / 2 ? 18 : -10}
                                fontSize={8}
                                textAnchor="middle"
                                fill="var(--panel-label)"
                              >
                                {t.label}
                              </text>
                            )}
                          </>
                        );
                      })()}
                    </g>
                  );
                })}

              </g>
            );
          })}

          {/* wires: one clean quadratic route per wire, rendered once above device photos. */}
          {project.wires.map((w) => {
            if (!wireHasVisibleEndpoints(w)) return null;
            const [fd, ft] = w.from.split(":");
            const [td, tt] = w.to.split(":");
            const a = project.devices.find((d) => d.id === fd);
            const b = project.devices.find((d) => d.id === td);
            if (!a || !b) return null;
            const p1 = terminalPos(a, ft!);
            const p2 = terminalPos(b, tt!);
            if (!p1 || !p2) return null;
            const control = w.control ?? defaultWireControl(p1, p2);
            const energized = live.has(w.from) && live.has(w.to);
            const dPath = `M${p1.x},${p1.y} Q${control.x},${control.y} ${p2.x},${p2.y}`;
            return (
              <g key={w.id} pointerEvents="none">
                <path
                  d={dPath}
                  fill="none"
                  stroke={wireColor(w.color)}
                  strokeWidth={selectedWire === w.id ? 5 : 3.4}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  opacity={0.96}
                />
                {energized && store.status === "RUN" && (
                  <path
                    d={dPath}
                    fill="none"
                    stroke="#39e04f"
                    strokeWidth={1.6}
                    strokeDasharray="6 8"
                    strokeLinecap="round"
                    opacity={0.9}
                  />
                )}
              </g>
            );
          })}

          {/* Keep terminal taps and photographic terminal feedback above the
              wire layer. This makes the exact screw circle stay visible and
              clickable even when a wire ends on it. */}
          {project.devices.map((d) => {
            const def = DEF[d.type];
            if (!def) return null;
            return (
              <g key={`terminal-hit-${d.id}`} transform={`translate(${d.x},${d.y})`}>
                {def.terminals.map((t) => {
                  if (d.type === "plc" && PLC_HIDDEN_TERMINALS.has(t.id)) return null;
                  const key = tk(d.id, t.id);
                  const isLive = live.has(key);
                  const isPending = pending === key;
                  const wired = project.wires.some((wire) => wire.from === key || wire.to === key);
                  const isMcbPhoto = d.type === "mcb1" || d.type === "mcb" || d.type === "mcb3";
                  const photoTerminal = d.type === "plc" || d.type === "contactor" || d.type === "relay" || d.type === "overload" || d.type === "timer" || isMcbPhoto;
                  const hitR = d.type === "plc" ? PLC_TERMINAL_HIT_R : d.type === "contactor" ? 9 : d.type === "relay" ? 5.2 : d.type === "overload" ? 7.8 : d.type === "timer" ? 6.5 : isMcbPhoto ? 8.5 : 12;
                  const terminalActive = d.type === "contactor" || d.type === "relay" || d.type === "overload" || d.type === "timer" || isMcbPhoto ? wired || isLive : isLive;
                  return (
                    <g key={t.id} transform={`translate(${t.x},${t.y})`}>
                      {d.type === "overload" ? (
                        <g pointerEvents="none">
                          {store.status === "RUN" && isLive && (
                            <circle r={9.8} fill="#37f56a" opacity={0.22} />
                          )}
                          <circle
                            r={7.8}
                            fill={wired ? "#22c55e" : "none"}
                            fillOpacity={wired ? 0.88 : 0}
                            stroke={store.status === "RUN" && isLive ? "#37f56a" : "#22c55e"}
                            strokeWidth={1.35}
                            opacity={wired || (store.status === "RUN" && isLive) ? 1 : 0.82}
                          />
                          {isPending && (
                            <circle r={4} fill="none" stroke="var(--color-primary)" strokeWidth={2.5} />
                          )}
                        </g>
                      ) : (
                        <>
                          {photoTerminal && terminalActive && (
                            <circle
                              r={d.type === "contactor" || d.type === "relay" || d.type === "timer" || isMcbPhoto ? hitR : Math.max(3.8, hitR - 1.2)}
                              fill="#32e34f"
                              fillOpacity={1}
                              stroke={d.type === "contactor" || d.type === "relay" || d.type === "timer" || isMcbPhoto ? "#d8ffdc" : "#b9ffc3"}
                              strokeWidth={d.type === "contactor" || d.type === "relay" || d.type === "timer" || isMcbPhoto ? 1 : 0.8}
                              pointerEvents="none"
                            />
                          )}
                          {photoTerminal && isPending && !terminalActive && (
                            <circle
                              r={Math.max(4.2, hitR - 0.5)}
                              fill="transparent"
                              stroke="#ffb020"
                              strokeWidth={1.4}
                              strokeDasharray="2 2"
                              pointerEvents="none"
                            />
                          )}
                        </>
                      )}
                      <circle
                        r={hitR}
                        fill="transparent"
                        pointerEvents="all"
                        onPointerDown={(e) => {
                          e.stopPropagation();
                          clickTerminal(d.id, t.id);
                        }}
                        style={{ cursor: "crosshair" }}
                      />
                    </g>
                  );
                })}
              </g>
            );
          })}
          {/* Wire interaction is deliberately the top hit layer. This gives the
              wire priority over PLC/contactor terminal hit areas, so a wire
              between I/O points can always be selected, routed and deleted. */}
          {project.wires.map((w) => {
            if (!wireHasVisibleEndpoints(w)) return null;
            const [fd, ft] = w.from.split(":");
            const [td, tt] = w.to.split(":");
            const a = project.devices.find((d) => d.id === fd);
            const b = project.devices.find((d) => d.id === td);
            if (!a || !b) return null;
            const p1 = terminalPos(a, ft!);
            const p2 = terminalPos(b, tt!);
            if (!p1 || !p2) return null;
            const control = w.control ?? defaultWireControl(p1, p2);
            const dPath = `M${p1.x},${p1.y} Q${control.x},${control.y} ${p2.x},${p2.y}`;
            return (
              <g key={`wire-hit-${w.id}`}>
                <path
                  d={dPath}
                  fill="none"
                  stroke="rgba(0,0,0,0.001)"
                  strokeWidth={26}
                  pointerEvents="stroke"
                  onPointerDown={(e) => {
                    e.stopPropagation();
                    setSelectedWire(w.id);
                    setSelected(null);
                    setPending(null);
                    const start = toWorld(e.clientX, e.clientY);
                    if (!start) return;
                    try {
                      (e.currentTarget as SVGPathElement).setPointerCapture?.(e.pointerId);
                    } catch {
                      // Mobile browsers can cancel capture during gesture changes.
                    }
                    store.beginWireMove();
                    wirePathDrag.current = {
                      id: w.id,
                      pointerId: e.pointerId,
                      startX: start.x,
                      startY: start.y,
                      controlX: control.x,
                      controlY: control.y,
                    };
                  }}
                  onPointerMove={(e) => {
                    const activeDrag = wirePathDrag.current;
                    if (!activeDrag || activeDrag.id !== w.id || activeDrag.pointerId !== e.pointerId) return;
                    e.stopPropagation();
                    const pt = toWorld(e.clientX, e.clientY);
                    if (!pt) return;
                    store.moveWireControl(
                      w.id,
                      activeDrag.controlX + (pt.x - activeDrag.startX),
                      activeDrag.controlY + (pt.y - activeDrag.startY),
                    );
                  }}
                  onPointerUp={(e) => {
                    const activeDrag = wirePathDrag.current;
                    if (activeDrag?.id === w.id && activeDrag.pointerId === e.pointerId) {
                      wirePathDrag.current = null;
                      try {
                        (e.currentTarget as SVGPathElement).releasePointerCapture?.(e.pointerId);
                      } catch {
                        // Safe no-op if capture was already released by the browser.
                      }
                    }
                  }}
                  onPointerCancel={(e) => {
                    const activeDrag = wirePathDrag.current;
                    if (activeDrag?.id === w.id && activeDrag.pointerId === e.pointerId) {
                      wirePathDrag.current = null;
                    }
                  }}
                  style={{ cursor: wirePathDrag.current?.id === w.id ? "grabbing" : "grab" }}
                />
              </g>
            );
          })}

          {/* Endpoint taps are the final/top hit layer. This keeps a terminal
              reusable after the first connection: tap the same terminal again
              and fan out as many additional wires as the circuit needs. */}
          {project.devices.map((d) => {
            const def = DEF[d.type];
            if (!def) return null;
            return (
              <g key={`terminal-top-hit-${d.id}`} transform={`translate(${d.x},${d.y})`}>
                {def.terminals.map((t) => {
                  if (d.type === "plc" && PLC_HIDDEN_TERMINALS.has(t.id)) return null;
                  const hitR = d.type === "plc" ? Math.max(6, PLC_TERMINAL_HIT_R - 1) : d.type === "contactor" ? 7.5 : d.type === "relay" ? 4.8 : d.type === "overload" ? 7.8 : d.type === "timer" ? 6.5 : (d.type === "mcb1" || d.type === "mcb" || d.type === "mcb3") ? 7.5 : 8;
                  return (
                    <circle
                      key={t.id}
                      cx={t.x}
                      cy={t.y}
                      r={hitR}
                      fill="transparent"
                      pointerEvents="all"
                      onPointerDown={(e) => {
                        e.stopPropagation();
                        clickTerminal(d.id, t.id);
                      }}
                      style={{ cursor: "crosshair" }}
                    />
                  );
                })}
              </g>
            );
          })}

        </g>
      </svg>

      {/* floating controls */}
      <div className="absolute bottom-3 left-3 flex gap-1">
        <button className="sim-btn" onClick={() => zoomBy(1.2)}>
          +
        </button>
        <button className="sim-btn" onClick={() => zoomBy(1 / 1.2)}>
          −
        </button>
        <button className="sim-btn" onClick={fit}>
          ملاءمة
        </button>
        <button className="sim-btn" onClick={() => zoomTo(1)}>
          100%
        </button>
        <button
          className={`sim-btn ${store.snap ? "border-primary text-primary" : ""}`}
          onClick={() => store.setSnap(!store.snap)}
          title="Snap to Grid"
        >
          شبكة
        </button>
        <span className="sim-chip">{Math.round(view.k * 100)}%</span>
      </div>

      {pending && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 rounded-md bg-primary px-3 py-1.5 text-xs text-primary-foreground shadow">
          اختر الطرف الثاني لإكمال التوصيل — {pendingLabel}
        </div>
      )}

      {selectedWire && (
        <div className="absolute bottom-3 right-3 z-50 flex items-center gap-1 rounded-md border border-border bg-card/98 p-2 shadow-xl backdrop-blur">
          {WIRE_COLORS.map((c) => (
            <button
              key={c.id}
              title={c.label}
              onClick={() => store.setWireColor(selectedWire, c.id)}
              className="h-6 w-6 rounded-full border border-border"
              style={{ background: c.css }}
            />
          ))}
          <button
            className="sim-btn text-destructive"
            onClick={() => {
              store.removeWire(selectedWire);
              setSelectedWire(null);
            }}
          >
            حذف السلك
          </button>
        </div>
      )}
    </div>
  );
}
